<script lang="ts">
  /**
   * Zonenchat: the chat of the district you are in (amendment 16).
   *
   * Newest at the bottom, as every chat. Your own messages can be taken back, anybody else's
   * reported — two people's reports hide one until an admin looks — and an admin can remove
   * any. The switch at the top is the opt-in from the amendment: with it on, this device tells
   * the server which district it is in, and the others' messages reach you as a push.
   *
   * 🎲 opens the games. An invitation to Tic-Tac-Toe or Schere, Stein, Papier goes into the chat
   * for whoever takes it first, and the magische Miesmuschel turns the next message into a
   * question that it answers in front of everybody (amendment 19). The drinking games are for the
   * whole round — Heiß oder kalt, Waben-Bingo, the Stundenwette and the Wabenwürfel (amendment
   * 20) — and start with a single tap.
   */
  import { untrack } from 'svelte';
  import { DUEL_NAMES, type Duel } from '$domain/games.ts';
  import { PARTY_NAMES, type PartyGame } from '$domain/party.ts';
  import { MESSAGE_MAX } from '$domain/zones.ts';
  import Avatar from './Avatar.svelte';
  import { cachedPlace, reverseGeocode } from './place.ts';
  import { describeError, session } from './session.svelte.ts';
  import ZoneGame from './ZoneGame.svelte';
  import ZoneParty from './ZoneParty.svelte';
  import { isDuelView, type ZoneMessage, zone } from './zone.svelte.ts';

  type Props = { onclose: () => void };
  const { onclose }: Props = $props();

  type Entry = { kind: Duel | PartyGame; signs: string; name: string; about: string };

  /** The games menu: who plays, then what. The Miesmuschel follows the last section by itself. */
  const MENU: { title: string; entries: Entry[] }[] = [
    {
      title: 'Zu zweit',
      entries: [
        { kind: 'tictactoe', signs: '❌⭕', name: DUEL_NAMES.tictactoe, about: 'Wer zuerst tippt' },
        { kind: 'rps', signs: '✊✋✌️', name: DUEL_NAMES.rps, about: 'Zwei Gewinnrunden' },
      ],
    },
    {
      title: 'Für die ganze Runde',
      entries: [
        { kind: 'hotcold', signs: '🔥🧊', name: PARTY_NAMES.hotcold, about: 'Minderheit trinkt' },
        {
          kind: 'bingo',
          signs: '🎱',
          name: PARTY_NAMES.bingo,
          about: 'Eigene Felder, zwei bestätigen',
        },
        { kind: 'bet', signs: '⏳', name: PARTY_NAMES.bet, about: 'Heißeste Wabe, eine Stunde' },
      ],
    },
    {
      title: 'Sofort',
      entries: [
        { kind: 'dice', signs: '🎲', name: PARTY_NAMES.dice, about: 'Sechs Seiten, sechs Aufgaben' },
      ],
    },
  ];

  let draft = $state('');
  let sending = $state(false);
  let error = $state('');
  let place = $state<string | undefined>();
  let list: HTMLOListElement | undefined = $state();
  let field: HTMLTextAreaElement | undefined = $state();
  /** The games menu above the field. */
  let games = $state(false);
  /** The next message goes to the Miesmuschel. */
  let asking = $state(false);

  const admin = $derived(session.user?.role === 'admin');
  const remaining = $derived(MESSAGE_MAX - [...draft].length);
  const elsewhere = $derived(
    zone.shown !== undefined && zone.here !== undefined && zone.shown !== zone.here,
  );

  // The district's name — "Stuttgart-Mitte" rather than an index — from the same lookup the
  // flags use, cached after the first time.
  $effect(() => {
    const shown = zone.shown;
    if (shown === undefined) return;
    place = cachedPlace(shown);
    if (place !== undefined) return;
    let cancelled = false;
    void reverseGeocode(shown).then((name) => {
      if (!cancelled) place = name;
    });
    return () => {
      cancelled = true;
    };
  });

  // Whatever arrives while the chat is open is read, and the list follows it down. Untracked:
  // marking reads the very record it writes, and tracked, every write would run this again —
  // until Svelte gives up with effect_update_depth_exceeded, which it did on every new message.
  //
  // Down only when a new message is last: a move in a game, or a poll that reads the games
  // again, replaces the list without adding to it, and must not scroll away from the board
  // somebody is playing on.
  let lastShown: string | undefined;
  $effect(() => {
    const last = zone.messages.at(-1)?.id;
    if (last === undefined) return;
    untrack(() => zone.markRead());
    if (last === lastShown) return;
    lastShown = last;
    queueMicrotask(() => list?.lastElementChild?.scrollIntoView({ block: 'end' }));
  });

  // The keyboard makes the page shorter, and the list with it. While somebody is typing, keep
  // its end in view — otherwise the message they are answering slides away under the field.
  $effect(() => {
    const element = list;
    if (element === undefined || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      if (document.activeElement === field) element.scrollTop = element.scrollHeight;
    });
    observer.observe(element);
    return () => observer.disconnect();
  });

  async function send(event: SubmitEvent) {
    event.preventDefault();
    const text = draft.trim();
    if (text === '' || remaining < 0) return;
    sending = true;
    error = '';
    try {
      if (asking) await zone.ask(text);
      else await zone.send(text);
      draft = '';
      asking = false;
    } catch (cause) {
      error = describeError(cause);
    } finally {
      sending = false;
    }
  }

  async function invite(kind: Duel | PartyGame) {
    games = false;
    sending = true;
    error = '';
    try {
      await zone.invite(kind);
    } catch (cause) {
      error = describeError(cause);
    } finally {
      sending = false;
    }
  }

  function askConch() {
    games = false;
    asking = true;
    field?.focus();
  }

  /** Your own invitation that nobody took yet is withdrawn rather than deleted. */
  function removeLabel(entry: ZoneMessage): string {
    const game = entry.game;
    return entry.mine && isDuelView(game) && game.status === 'open' ? 'Zurückziehen' : 'Löschen';
  }

  async function act(entry: ZoneMessage, verb: 'remove' | 'report') {
    error = '';
    try {
      if (verb === 'remove') await zone.remove(entry.id);
      else {
        await zone.report(entry.id);
        error = 'Gemeldet. Melden zwei Leute dieselbe Nachricht, sieht sie sich ein Admin an.';
      }
    } catch (cause) {
      error = describeError(cause);
    }
  }

  const timeOf = (iso: string) => {
    const at = new Date(iso);
    const today = at.toDateString() === new Date().toDateString();
    return at.toLocaleString('de-DE', {
      ...(today ? {} : { day: 'numeric', month: 'numeric' }),
      hour: '2-digit',
      minute: '2-digit',
    });
  };
</script>

<section class="panel" aria-label="Zonenchat">
  <header>
    <h2>💬 {place ?? 'Dein Viertel'}</h2>
    <button class="close" onclick={onclose} aria-label="Schließen">×</button>
  </header>

  <!-- The whole explanation while the choice is still to be made; once it is made, one line
       that says so, and the conversation gets the room back. -->
  <label class="share" class:on={zone.sharing}>
    <input
      type="checkbox"
      checked={zone.sharing}
      onchange={(event) => void zone.setSharing(event.currentTarget.checked)}
    />
    {#if zone.sharing}
      <span><strong>Viertel geteilt</strong> · nur das Viertel, nach 2 h vergessen</span>
    {:else}
      <span>
        <strong>Mein Viertel teilen</strong>, damit mich neue Nachrichten erreichen.
        <small
          >Der Server erfährt dann nur das Viertel, rund zweieinhalb Kilometer breit, und
          vergisst es zwei Stunden nach dem letzten Mal.</small
        >
      </span>
    {/if}
  </label>

  {#if zone.problem && zone.shown === undefined}
    <p class="hint" role="status">{zone.problem}</p>
  {:else if zone.shown === undefined}
    <p class="hint">Einen Moment, die App sucht dein Viertel …</p>
  {:else}
    {#if elsewhere}
      <p class="hint">Du bist gerade nicht mehr in diesem Viertel.</p>
    {/if}

    {#if zone.messages.length === 0}
      <p class="hint empty">Noch ist es still hier. Schreib als Erste etwas.</p>
    {:else}
      <ol class="messages" bind:this={list}>
        {#each zone.messages as entry (entry.id)}
          <li class:mine={entry.mine}>
            <p class="who">
              <Avatar username={entry.author.username} emoji={entry.author.avatarEmoji} />
              @{entry.author.username}
              <time datetime={entry.createdAt}>{timeOf(entry.createdAt)}</time>
            </p>
            {#if entry.party !== null}
              <ZoneParty message={entry} />
            {:else if entry.game !== null}
              <ZoneGame message={entry} />
            {:else}
              <p class="body">{entry.body}</p>
            {/if}
            <div class="verbs">
              {#if entry.mine || admin}
                <button class="quiet" onclick={() => act(entry, 'remove')}>
                  {removeLabel(entry)}
                </button>
              {/if}
              <!-- An invitation has no words of its own to report; a question to the
                   Miesmuschel does. -->
              {#if !entry.mine && (entry.kind === 'text' || entry.kind === 'conch')}
                <button
                  class="quiet"
                  onclick={() => act(entry, 'report')}
                  title="Melden zwei Leute dieselbe Nachricht, verschwindet sie, bis ein Admin sie ansieht."
                >
                  Melden
                </button>
              {/if}
            </div>
          </li>
        {/each}
      </ol>
    {/if}

    {#if error}<p class="hint error" role="status">{error}</p>{/if}

    {#if games}
      <div class="games" role="group" aria-label="Spiele">
        {#each MENU as section (section.title)}
          <p class="section">{section.title}</p>
          {#each section.entries as entry (entry.kind)}
            <button disabled={sending} onclick={() => invite(entry.kind)}>
              <span class="signs" aria-hidden="true">{entry.signs}</span>
              <span>
                <strong>{entry.name}</strong>
                <small>{entry.about}</small>
              </span>
            </button>
          {/each}
        {/each}
        <button onclick={askConch}>
          <span class="signs" aria-hidden="true">🐚</span>
          <span>
            <strong>Miesmuschel</strong>
            <small>Antwortet vor allen</small>
          </span>
        </button>
        <p class="water">🍻 Ein Schluck darf immer auch Wasser sein.</p>
      </div>
    {/if}

    <form onsubmit={send}>
      {#if asking}
        <p class="asking">
          🐚 Deine Frage an die magische Miesmuschel
          <button
            type="button"
            class="quiet"
            onclick={() => (asking = false)}
            aria-label="Doch keine Frage">×</button
          >
        </p>
      {/if}
      <!-- One row, the way every chat has it: the games, the field, the button. The field grows
           with what is typed, and the second row it used to sit above is the chat's again. -->
      <div class="send">
        <button
          type="button"
          class="play"
          aria-expanded={games}
          aria-label="Spielen"
          title="Spielen"
          onclick={() => (games = !games)}
        >
          🎲
        </button>
        <span class="field">
          <textarea
            bind:this={field}
            bind:value={draft}
            rows="1"
            placeholder={asking ? 'Eine Ja-oder-Nein-Frage …' : 'Was ist hier gerade los?'}
            aria-label={asking ? 'Frage an die Miesmuschel' : 'Nachricht'}
            onkeydown={(event) => {
              // Enter sends, Shift+Enter breaks the line — what every chat has taught everybody.
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                event.currentTarget.form?.requestSubmit();
              }
            }}
          ></textarea>
          {#if remaining < 40}<small class:over={remaining < 0}>{remaining}</small>{/if}
        </span>
        <button type="submit" disabled={sending || draft.trim() === '' || remaining < 0}>
          {asking ? 'Fragen' : 'Senden'}
        </button>
      </div>
    </form>
  {/if}
</section>

<style>
  .panel {
    position: absolute;
    inset: max(3.5rem, env(safe-area-inset-top)) 1rem max(1rem, env(safe-area-inset-bottom));
    z-index: 4;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    max-width: 30rem;
    margin-inline: auto;
    padding: 0.75rem 1rem 1rem;
    border: 1px solid var(--border);
    border-radius: 1rem;
    background: var(--card);
    box-shadow: 0 8px 32px rgb(0 0 0 / 30%);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  h2 {
    margin: 0;
    font-size: 1.1rem;
  }

  .close {
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.75rem;
    line-height: 1;
    cursor: pointer;
  }

  .share {
    display: flex;
    gap: 0.55rem;
    align-items: flex-start;
    padding: 0.5rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    font-size: 0.8125rem;
    cursor: pointer;
  }

  .share input {
    margin-top: 0.15rem;
  }

  .share.on {
    align-items: center;
    padding: 0.3rem 0.6rem;
    color: var(--muted);
    font-size: 0.75rem;
  }

  .share.on input {
    margin-top: 0;
  }

  .share.on strong {
    color: var(--fg);
  }

  /* While somebody types, the keyboard takes half the screen: the setting steps aside for the
     conversation, and is back the moment the field is left. */
  .panel:has(textarea:focus) .share {
    display: none;
  }

  .share small {
    display: block;
    margin-top: 0.15rem;
    color: var(--muted);
  }

  .hint {
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .hint.empty {
    flex: 1;
  }

  .error {
    color: var(--accent);
  }

  .messages {
    flex: 1;
    margin: 0;
    padding: 0;
    overflow-y: auto;
    list-style: none;
  }

  .messages li {
    padding: 0.45rem 0.2rem;
    border-top: 1px solid var(--border);
  }

  .messages li.mine {
    border-left: 3px solid var(--accent);
    padding-left: 0.6rem;
  }

  .messages p {
    margin: 0;
  }

  .who {
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .who time {
    margin-left: 0.35rem;
    font-size: 0.75rem;
  }

  .body {
    margin-top: 0.1rem;
    font-size: 0.9375rem;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .verbs {
    display: flex;
    gap: 0.4rem;
    justify-content: flex-end;
  }

  .verbs button {
    padding: 0.1rem 0.45rem;
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 0.75rem;
    cursor: pointer;
  }

  /* Two columns of tiles, the sections across both. On a short screen it scrolls by itself
     rather than pushing the field off it. */
  .games {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.35rem;
    max-height: 46dvh;
    overflow-y: auto;
  }

  .games .section,
  .games .water {
    grid-column: 1 / -1;
    margin: 0.15rem 0 0;
    color: var(--muted);
    font-size: 0.6875rem;
  }

  .games .section {
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .games .water {
    text-align: center;
  }

  .games button {
    display: flex;
    gap: 0.45rem;
    align-items: center;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font: inherit;
    text-align: left;
    cursor: pointer;
  }

  .games button:hover {
    border-color: color-mix(in srgb, var(--accent) 45%, var(--border));
  }

  /* A column of its own width, so that every name starts in the same place. Two signs fit it
     side by side; three hands do not, and break into two rows rather than run into the name. */
  .games .signs {
    flex: none;
    width: 2.9rem;
    font-size: 0.95rem;
    line-height: 1.15;
    text-align: center;
    word-break: break-all;
  }

  .games strong,
  .games small {
    display: block;
  }

  .games strong {
    font-size: 0.8125rem;
    line-height: 1.2;
  }

  .games small {
    color: var(--muted);
    font-size: 0.6875rem;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .asking {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 0;
    font-size: 0.8125rem;
    color: var(--muted);
  }

  .asking button {
    padding: 0 0.3rem;
    border: 0;
    background: none;
    color: var(--muted);
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
  }

  .send {
    display: flex;
    align-items: flex-end;
    gap: 0.4rem;
  }

  .field {
    position: relative;
    flex: 1;
    min-width: 0;
  }

  textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    /* One line to begin with, growing with what is typed up to about five, then scrolling. A
       browser without field-sizing keeps the one line and scrolls in it. */
    field-sizing: content;
    min-height: 2.4rem;
    max-height: 7.5rem;
    padding: 0.5rem 0.6rem;
    border: 1px solid var(--border);
    border-radius: 0.6rem;
    background: var(--bg);
    color: var(--fg);
    font: inherit;
    font-size: 0.9375rem;
    line-height: 1.3;
    resize: none;
  }

  /* What is left of the message, in the field's corner once it starts to matter. */
  .field small {
    position: absolute;
    right: 0.45rem;
    bottom: 0.15rem;
    color: var(--muted);
    font-size: 0.6875rem;
    pointer-events: none;
  }

  .field small.over {
    color: var(--accent);
  }

  .send button {
    flex: none;
    height: 2.4rem;
    padding: 0 0.9rem;
    border: 1px solid var(--accent);
    border-radius: 0.6rem;
    background: color-mix(in srgb, var(--accent) 16%, var(--bg));
    color: var(--fg);
    font-size: 0.875rem;
    cursor: pointer;
  }

  .send button:disabled {
    opacity: 0.5;
    cursor: default;
  }

  /* The games sit on the far side from Senden, and look like the lesser button they are. */
  .send .play {
    width: 2.4rem;
    padding: 0;
    border-color: var(--border);
    background: var(--bg);
    font-size: 1.1rem;
  }

  /* A short screen: the keyboard is open, or the phone lies on its side. The panel takes the
     whole height, the logo above it included, and its title one line, so that the conversation
     keeps what room is left. */
  @media (max-height: 560px) {
    .panel {
      top: max(0.5rem, env(safe-area-inset-top));
      bottom: max(0.5rem, env(safe-area-inset-bottom));
      gap: 0.35rem;
      padding: 0.5rem 0.75rem 0.6rem;
    }

    h2 {
      overflow: hidden;
      font-size: 0.95rem;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .close {
      font-size: 1.5rem;
    }
  }
</style>
